import { useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { BrandSiteAssetPicker } from "@/components/brand-site/BrandSiteAssetPicker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type Props = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  accountId: string | number;
  brandId: number;
  brandSiteId: number;
  /** 写入 reference context，如 blog/slug */
  pageSlug?: string | null;
  publicUrl?: string | null;
  assetBaseUrl?: string | null;
};

/** pick 模式已回填站点代发路径；封面缩略图直接拼品牌站公网 origin。 */
function coverPreviewUrl(
  value: string,
  baseUrl: string | null | undefined,
): string | null {
  const path = value.trim();
  if (/^https?:\/\//i.test(path)) return path;
  const base = baseUrl?.trim().replace(/\/+$/, "");
  return path.startsWith("/") && base ? `${base}${path}` : null;
}

/** 封面等图片字段：选图器回填 /_c456/a/…，可清空 */
export function BrandSiteCoverField({
  id,
  label,
  value,
  onChange,
  accountId,
  brandId,
  brandSiteId,
  pageSlug = null,
  publicUrl = null,
  assetBaseUrl = null,
}: Props) {
  const [open, setOpen] = useState(false);
  const preview = coverPreviewUrl(value, publicUrl ?? assetBaseUrl);

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex flex-wrap items-start gap-3">
        <div
          className={cn(
            "flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted/40",
            !preview && "text-muted-foreground",
          )}
        >
          {preview ? (
            <img src={preview} alt="" className="size-full object-cover" />
          ) : (
            <ImagePlus className="size-5 opacity-50" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          {value.trim() ? (
            <p className="truncate font-mono text-[11px] text-muted-foreground" title={value}>
              {value}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">未选封面，可留空</p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
              <ImagePlus className="size-3.5" />
              {value.trim() ? "更换图片" : "选择图片"}
            </Button>
            {value.trim() ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange("")}
                aria-label="清除封面"
              >
                <X className="size-3.5" />
                清除
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      <BrandSiteAssetPicker
        open={open}
        onOpenChange={setOpen}
        accountId={String(accountId)}
        brandId={brandId}
        brandSiteId={brandSiteId}
        pageSlug={pageSlug}
        publicUrl={publicUrl}
        mode="pick"
        onPick={(proxyPath) => onChange(proxyPath)}
      />
    </div>
  );
}
